/*
 * Purpose: AutoBlocks compose session — Mission (running) vs Flow (at rest)
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { create } from 'zustand';
import { planFromOutcomeAsync } from '@/services/autoblocks/planFromOutcomeAsync';
import { attachWorkflowPreview } from '@/services/autoblocks/workflowAssembler';
import { runDryRun } from '@/services/autoblocks/dryRun';
import { assembleWorkflowFromPlan } from '@/services/autoblocks/workflowAssembler';
import { expandOutcomeWithDirector, applyDirectorStrategy } from '@/services/autoblocks/director';
import { suggestBrandedBlockIdeas } from '@/lib/brandedBlocks';
import { interpretOutcomeAsync } from '@/services/autoblocks/intentInterpretation';
import type { IntentInterpretation } from '@/services/autoblocks/intentInterpretation';
import {
  resolveInitialComposeMode,
  shouldSwitchToGuided,
  shouldSwitchToExpress,
} from '@/services/autoblocks/flowMode';
import {
  createMissionStep,
  type ArtifactState,
  type ComposeMode,
  type MissionStep,
} from '@/services/autoblocks/missionTypes';
import { auditPlanReadiness } from '@/services/autoblocks/planReadiness';
import type { AutoBlocksPhase, DirectorExpansion, DryRunReport, OutcomePlan } from '@/services/autoblocks/types';
import { useWorkflowStore } from '@/stores/workflowStore';
import { persistWorkflows } from '@/lib/workflowUtils';
import { rehydratePlanConnectors } from '@/services/autoblocks/planReadiness';
import { toast } from 'sonner';

interface AutoBlocksStore {
  phase: AutoBlocksPhase;
  artifactState: ArtifactState;
  composeMode: ComposeMode | null;
  missionTrail: MissionStep[];
  outcomeText: string;
  interpretation: IntentInterpretation | null;
  plan: OutcomePlan | null;
  director: DirectorExpansion | null;
  dryRunReport: DryRunReport | null;
  isInterpreting: boolean;
  isDirectorWorking: boolean;
  isPlanning: boolean;
  isDryRunning: boolean;
  error: string | null;

  setOutcomeText: (text: string) => void;
  startMission: (text: string) => void;
  interpretOutcome: () => Promise<void>;
  confirmAndPlan: () => Promise<void>;
  expressLaneProceed: () => Promise<void>;
  setComposeMode: (mode: ComposeMode) => void;
  appendMissionTrail: (step: MissionStep) => void;
  directorExpandAndPlan: () => Promise<void>;
  planOutcome: () => Promise<void>;
  runDryRunTest: () => Promise<void>;
  approveAndOpenInBuilder: () => void;
  reset: () => void;
}

function pushTrail(
  trail: MissionStep[],
  kind: MissionStep['kind'],
  label: string,
  detail?: string
): MissionStep[] {
  return [...trail, createMissionStep(kind, label, detail)];
}

export const useAutoBlocksStore = create<AutoBlocksStore>((set, get) => ({
  phase: 'describe',
  artifactState: 'mission',
  composeMode: null,
  missionTrail: [],
  outcomeText: '',
  interpretation: null,
  plan: null,
  director: null,
  dryRunReport: null,
  isInterpreting: false,
  isDirectorWorking: false,
  isPlanning: false,
  isDryRunning: false,
  error: null,

  setOutcomeText: (text) => {
    const { plan, outcomeText: prev } = get();
    const materiallyChanged = plan && text.trim() !== prev.trim();
    if (materiallyChanged) {
      set({
        outcomeText: text,
        error: null,
        artifactState: 'mission',
        composeMode: null,
        plan: null,
        director: null,
        dryRunReport: null,
        interpretation: null,
        phase: 'describe',
        missionTrail: pushTrail(get().missionTrail, 'capture', 'Outcome edited — Mission restarted'),
      });
      return;
    }
    set({ outcomeText: text, error: null });
  },

  startMission: (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    set({
      outcomeText: trimmed,
      artifactState: 'mission',
      composeMode: null,
      phase: 'describe',
      interpretation: null,
      plan: null,
      director: null,
      dryRunReport: null,
      error: null,
      missionTrail: [createMissionStep('capture', 'Mission started', trimmed.slice(0, 80))],
    });
  },

  appendMissionTrail: (step) => set({ missionTrail: [...get().missionTrail, step] }),

  setComposeMode: (mode) => {
    const { composeMode: prev, missionTrail } = get();
    if (prev === mode) return;
    set({
      composeMode: mode,
      missionTrail: pushTrail(
        missionTrail,
        'mode_switch',
        `Compose mode → ${mode === 'express' ? 'Express' : 'Guided'}`
      ),
    });
  },

  interpretOutcome: async () => {
    const { outcomeText, missionTrail } = get();
    const trimmed = outcomeText.trim();
    if (!trimmed) return;

    set({
      isInterpreting: true,
      error: null,
      phase: 'interpreting',
      artifactState: 'mission',
      composeMode: null,
    });

    try {
      const interpretation = await interpretOutcomeAsync(trimmed);
      const mode = resolveInitialComposeMode(interpretation);
      set({
        interpretation,
        composeMode: mode,
        phase: 'confirm',
        isInterpreting: false,
        missionTrail: pushTrail(
          pushTrail(missionTrail, 'interpret', 'Outcome interpreted', interpretation.interpretedGoal),
          'confirm',
          'Awaiting your confirmation',
          mode === 'express' ? 'Express lane suggested' : 'Guided trail suggested'
        ),
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Interpretation failed';
      set({ error: msg, isInterpreting: false, phase: 'describe' });
    }
  },

  directorExpandAndPlan: async () => {
    const { outcomeText, interpretation, missionTrail } = get();
    const trimmed = outcomeText.trim();
    if (!trimmed) return;

    set({ isDirectorWorking: true, isPlanning: true, error: null, phase: 'director' });

    try {
      await new Promise((r) => setTimeout(r, 400));

      const base = await planFromOutcomeAsync(trimmed);
      const director = expandOutcomeWithDirector(
        trimmed,
        interpretation?.interpretedGoal ?? base.interpretedGoal
      );
      const brandedIdeas = suggestBrandedBlockIdeas(trimmed);
      set({ director, phase: 'planning' });

      const strategized = applyDirectorStrategy({
        ...base,
        director,
        brandedIdeas: brandedIdeas.length ? brandedIdeas : undefined,
      });
      const plan = attachWorkflowPreview(rehydratePlanConnectors(strategized));

      const readiness = auditPlanReadiness(plan);
      let composeMode = get().composeMode ?? resolveInitialComposeMode(
        interpretation ?? {
          rawOutcome: trimmed,
          interpretedGoal: base.interpretedGoal,
          confidence: base.confidence,
          suggestedComposeMode: 'guided',
          assumptions: [],
          gaps: [],
          plannerSource: 'rules',
        }
      );

      if (shouldSwitchToGuided(readiness, interpretation) && composeMode === 'express') {
        composeMode = 'guided';
        set({
          composeMode,
          missionTrail: pushTrail(
            get().missionTrail,
            'blocker',
            'Switched to Guided — setup blockers detected'
          ),
        });
      } else if (shouldSwitchToExpress(readiness, interpretation) && composeMode === 'guided') {
        composeMode = 'express';
        set({
          composeMode,
          missionTrail: pushTrail(get().missionTrail, 'mode_switch', 'Readiness green — Express lane')
        });
      }

      set({
        plan,
        director,
        composeMode,
        phase: 'review',
        isDirectorWorking: false,
        isPlanning: false,
        missionTrail: pushTrail(
          pushTrail(missionTrail, 'plan', 'Mission map planned', `${plan.steps.length} steps`),
          'plan',
          director.pacing === 'journey_tube' ? 'Journey Tube pacing' : 'Immediate wiring'
        ),
      });

      if (director.pacing === 'journey_tube') {
        toast.message('Director left a Journey Tube to your outcome — natural pacing');
      } else if (plan.plannerSource === 'llm') {
        toast.success('Director wired blocks on the map — review and dry run');
      } else {
        toast.message('Director expanded your request — review the Mission map');
      }

      if (base.builtConnectors && base.builtConnectors.length > 0) {
        toast.success(`AI built ${base.builtConnectors.length} new connector(s)`);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Director planning failed';
      set({ error: msg, isDirectorWorking: false, isPlanning: false, phase: 'confirm' });
    }
  },

  confirmAndPlan: async () => {
    const { interpretation } = get();
    if (!interpretation) {
      await get().interpretOutcome();
      if (!get().interpretation) return;
    }
    const mode = get().composeMode ?? resolveInitialComposeMode(get().interpretation!);
    set({
      composeMode: mode,
      phase: mode === 'express' ? 'express_running' : 'planning',
      missionTrail: pushTrail(get().missionTrail, 'confirm', 'Outcome confirmed'),
    });
    if (mode === 'express') {
      await new Promise((r) => setTimeout(r, 300));
    }
    await get().directorExpandAndPlan();
  },

  expressLaneProceed: async () => {
    set({
      composeMode: 'express',
      phase: 'express_running',
      missionTrail: pushTrail(get().missionTrail, 'express', 'Express lane — confirm & plan'),
    });
    await new Promise((r) => setTimeout(r, 300));
    await get().directorExpandAndPlan();
  },

  planOutcome: async () => {
    const { phase, interpretation } = get();
    if (phase === 'describe' || !interpretation) {
      await get().interpretOutcome();
      return;
    }
    if (phase === 'confirm') {
      await get().confirmAndPlan();
    }
  },

  runDryRunTest: async () => {
    const { plan, missionTrail } = get();
    if (!plan) return;
    set({ isDryRunning: true, error: null, phase: 'dry_run' });
    try {
      const dryRunReport = await runDryRun(plan);
      set({
        dryRunReport,
        isDryRunning: false,
        phase: 'review',
        missionTrail: pushTrail(
          missionTrail,
          'dry_run',
          dryRunReport.passed ? 'Dry run passed' : 'Dry run found issues'
        ),
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Dry run failed';
      set({ error: msg, isDryRunning: false });
    }
  },

  approveAndOpenInBuilder: () => {
    const { plan, missionTrail } = get();
    if (!plan) return;

    const workflow = assembleWorkflowFromPlan(plan);
    const wfState = useWorkflowStore.getState();
    const workflows = [...wfState.workflows, workflow];
    persistWorkflows(workflows);
    useWorkflowStore.setState({
      workflows,
      currentWorkflow: workflow,
      selectedNode: null,
    });
    set({
      phase: 'approved',
      artifactState: 'flow',
      composeMode: null,
      missionTrail: pushTrail(missionTrail, 'approve', 'Flow validated — at rest'),
    });
    toast.success('Your Flow is ready — open Flow Map in Builder');
  },

  reset: () =>
    set({
      phase: 'describe',
      artifactState: 'mission',
      composeMode: null,
      missionTrail: [],
      outcomeText: '',
      interpretation: null,
      plan: null,
      director: null,
      dryRunReport: null,
      isInterpreting: false,
      isDirectorWorking: false,
      isPlanning: false,
      isDryRunning: false,
      error: null,
    }),
}));
